import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api from "@/lib/api";
import { fmtMYR, fmtL, fmtDate, monthStr, PRODUCT_COLORS } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie,
} from "recharts";
import { Loader2, TrendingUp, TrendingDown, AlertCircle, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

export default function PnL() {
  const { stationId: station, setStationId: setStation, stations } = useStation();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState("all");
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  useEffect(() => {
    api.get("/data-months").then((r) => {
      const m = r.data || [];
      setMonth(m.length ? m[m.length - 1] : monthStr());
    }).catch(() => setMonth(monthStr()));
  }, []);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    const q = `${station === "all" ? "" : `&station_id=${station}`}${product === "all" ? "" : `&product=${encodeURIComponent(product)}`}`;
    api.get(`/pnl?month=${month}${q}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [station, month, product]);

  const s = data?.summary;
  const profitable = s?.net_profit >= 0;

  const download = async (format) => {
    try {
      const q = station === "all" ? "" : `&station_id=${station}`;
      const res = await api.get(`/pnl/export?month=${month}&format=${format}${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pnl-${month}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`P&L exported as ${format.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Financials</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">Monthly P&amp;L</h1>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Month</Label>
            <Input type="month" data-testid="pnl-month-input" value={month} onChange={(e) => setMonth(e.target.value)}
              className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Station</Label>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger data-testid="pnl-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-48"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Stations</SelectItem>
                {stations.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Product</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger data-testid="pnl-product-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Products</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => download("csv")} data-testid="export-csv-button"
            className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => download("xlsx")} data-testid="export-xlsx-button"
            className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={() => download("pdf")} data-testid="export-pdf-button"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </div>

      {loading || !s ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          {/* P&L waterfall banner */}
          <div className={`${card} p-6 sm:p-8`} data-testid="pnl-banner">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-6">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">Revenue</div>
                <div className="metric-value text-2xl font-bold text-white mt-1">{fmtMYR(s.revenue)}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">− Fuel Cost</div>
                <div className="metric-value text-2xl font-bold text-blue-400 mt-1">{fmtMYR(s.fuel_cost)}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">− Expenses</div>
                <div className="metric-value text-2xl font-bold text-amber-400 mt-1">{fmtMYR(s.expenses)}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">+ BUDI Claim</div>
                <div className="metric-value text-2xl font-bold text-emerald-300 mt-1" data-testid="pnl-budi-claim">{fmtMYR(s.budi_claim || 0)}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">subsidy owed to you</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">= Net Profit</div>
                <div className={`metric-value text-2xl font-bold mt-1 flex items-center gap-1.5 ${profitable ? "text-emerald-400" : "text-red-400"}`} data-testid="pnl-net-profit-metric">
                  {profitable ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  {fmtMYR(s.net_profit)}
                </div>
                <div className="text-xs text-[#9CA3AF] mt-1">{s.margin_pct}% margin · {fmtL(s.total_liters)} sold{s.budi_claim > 0 ? ` · incl. BUDI ${fmtMYR(s.budi_claim)}` : ""}</div>
              </div>
            </div>
            {Math.abs(s.cash_variance) > 1 && (
              <div className="mt-5 flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2" data-testid="cash-variance-alert">
                <AlertCircle className="w-4 h-4" />
                Cash variance: {fmtMYR(s.cash_variance)} ({s.cash_variance > 0 ? "over" : "short"} vs meter sales of {fmtMYR(s.revenue)})
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            <div className={`${card} p-5 sm:p-6 xl:col-span-2`} data-testid="pnl-daily-chart">
              <h3 className="font-[Outfit] font-semibold text-lg mb-4">Daily Revenue vs Cost</h3>
              {data.daily.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.daily} margin={{ left: -10, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 11 }} tickFormatter={(d) => d?.slice(8)} />
                    <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmtMYR(v)} contentStyle={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#9CA3AF" }} />
                    <Bar dataKey="revenue" name="Revenue" fill="#10B981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="cost" name="Fuel Cost" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="text-[#9CA3AF] text-sm py-20 text-center">No data for this month.</div>}
            </div>

            <div className={`${card} p-5 sm:p-6`} data-testid="pnl-volume-chart">
              <h3 className="font-[Outfit] font-semibold text-lg mb-4">Volume by Fuel Type</h3>
              {data.by_product.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={data.by_product} dataKey="liters" nameKey="product" cx="50%" cy="50%" outerRadius={95} label={(e) => e.product}>
                      {data.by_product.map((e) => <Cell key={e.product} fill={PRODUCT_COLORS[e.product] || "#10B981"} stroke="none" />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtL(v)} contentStyle={{ background: "#1F2937", border: "1px solid #374151", borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-[#9CA3AF] text-sm py-20 text-center">No sales recorded.</div>}
            </div>
          </div>

          <div className={`${card} p-5 sm:p-6`} data-testid="reconciliation-table">
            <h3 className="font-[Outfit] font-semibold text-lg mb-1">Daily Reconciliation</h3>
            <p className="text-xs text-[#9CA3AF] mb-4">Meter volume &amp; total sales vs cash collected each day.</p>
            {data.daily.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                      {["date", "volume", "total sales", "cash collected", "difference"].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily.map((d) => {
                      const diff = (d.cash || 0) - d.revenue;
                      return (
                        <tr key={d.date} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]">
                          <td className="py-2.5 pr-4 metric-value text-white/80">{fmtDate(d.date)}</td>
                          <td className="py-2.5 pr-4 metric-value">{fmtL(d.liters)}</td>
                          <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(d.revenue)}</td>
                          <td className="py-2.5 pr-4 metric-value">{fmtMYR(d.cash || 0)}</td>
                          <td className={`py-2.5 pr-4 metric-value ${diff < 0 ? "text-red-400" : diff > 0 ? "text-amber-400" : "text-emerald-400"}`}>
                            {fmtMYR(diff)}{diff < 0 ? " · short" : diff > 0 ? " · over" : ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[#1F2937] font-semibold">
                      <td className="py-2.5 pr-4 text-[#9CA3AF]">Total</td>
                      <td className="py-2.5 pr-4 metric-value">{fmtL(s.total_liters)}</td>
                      <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(s.revenue)}</td>
                      <td className="py-2.5 pr-4 metric-value">{fmtMYR(s.cash_collected)}</td>
                      <td className={`py-2.5 pr-4 metric-value ${s.cash_variance < 0 ? "text-red-400" : s.cash_variance > 0 ? "text-amber-400" : "text-emerald-400"}`}>{fmtMYR(s.cash_variance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : <div className="text-[#9CA3AF] text-sm py-8 text-center">No data for this month.</div>}
          </div>

          {data.expenses_by_category.length > 0 && (
            <div className={`${card} p-5 sm:p-6`} data-testid="pnl-expenses-breakdown">
              <h3 className="font-[Outfit] font-semibold text-lg mb-4">Expense Breakdown</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {data.expenses_by_category.map((e) => (
                  <div key={e.category} className="rounded-xl border border-[#1F2937] bg-[#0B0F17] p-4">
                    <div className="text-xs text-[#9CA3AF]">{e.category}</div>
                    <div className="metric-value text-lg font-semibold text-amber-400 mt-1">{fmtMYR(e.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
